import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Upload,
  X,
  CheckCircle2,
  Phone,
  ImagePlus,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { business, serviceTypeOptions } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass =
  "border border-charcoal/20 rounded-none bg-cream focus-visible:ring-rust focus-visible:ring-offset-0 h-11 text-base";

const timeOptions = [
  "As soon as possible",
  "Morning (7AM – 12PM)",
  "Afternoon (12PM – 5PM)",
  "Weekend",
  "Flexible",
];

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  property_address: "",
  property_type: "residential",
  service_type: "",
  description: "",
  preferred_contact: "Phone call",
  preferred_time: "",
  website: "", // honeypot
};

export default function QuoteForm({ defaultService = "" }) {
  const [form, setForm] = useState({ ...emptyForm, service_type: defaultService });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const data = new FormData();
      data.append("file", file);
      try {
        const res = await axios.post(`${API}/upload`, data);
        setPhotos((p) => [...p, res.data]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Could not upload ${file.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    if (!form.service_type) {
      toast.error("Please select the type of service needed.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, photos });
      if (res.data.success) {
        setSubmitted(true);
        toast.success("Request received! We'll be in touch shortly.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please call us at 215-806-5038.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="bg-cream border border-charcoal/20 shadow-md p-8 text-center"
        data-testid="quote-form-success"
      >
        <span className="inline-flex items-center justify-center w-16 h-16 bg-rust text-cream border border-charcoal/20 mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8" />
        </span>
        <h3 className="font-heading text-3xl uppercase text-charcoal">Thank You!</h3>
        <p className="mt-3 text-charcoal/75 max-w-md mx-auto leading-relaxed">
          Your request has been received. A member of the Future Roofing and Repair team will
          contact you shortly. For urgent roof leaks, please call us right away.
        </p>
        <a
          href={business.phoneHref}
          className="mt-6 inline-flex items-center gap-2 bg-rust text-cream border border-charcoal/20 shadow-md font-heading text-xl uppercase px-6 py-3"
        >
          <Phone className="w-5 h-5" /> Call {business.phone}
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="quote-form"
      className="bg-cream border border-charcoal/20 shadow-md p-6 md:p-8 space-y-5"
    >
      {/* Honeypot */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={form.website}
        onChange={(e) => update("website", e.target.value)}
        className="hidden"
        aria-hidden="true"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="qf-name" className="font-accent uppercase text-xs tracking-widest">
            Name *
          </Label>
          <Input
            id="qf-name"
            data-testid="qf-name"
            className={`${inputClass} mt-1.5`}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your full name"
            required
          />
        </div>
        <div>
          <Label htmlFor="qf-phone" className="font-accent uppercase text-xs tracking-widest">
            Phone Number *
          </Label>
          <Input
            id="qf-phone"
            data-testid="qf-phone"
            type="tel"
            className={`${inputClass} mt-1.5`}
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(215) 555-0123"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="qf-email" className="font-accent uppercase text-xs tracking-widest">
            Email Address *
          </Label>
          <Input
            id="qf-email"
            data-testid="qf-email"
            type="email"
            className={`${inputClass} mt-1.5`}
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            required
          />
        </div>
        <div>
          <Label htmlFor="qf-address" className="font-accent uppercase text-xs tracking-widest">
            Property Address
          </Label>
          <Input
            id="qf-address"
            data-testid="qf-address"
            className={`${inputClass} mt-1.5`}
            value={form.property_address}
            onChange={(e) => update("property_address", e.target.value)}
            placeholder="Street, City, State"
          />
        </div>
      </div>

      <div>
        <Label className="font-accent uppercase text-xs tracking-widest">Property Type</Label>
        <RadioGroup
          value={form.property_type}
          onValueChange={(v) => update("property_type", v)}
          className="flex gap-3 mt-2"
        >
          {[
            { v: "residential", l: "Residential" },
            { v: "commercial", l: "Commercial" },
          ].map((opt) => (
            <label
              key={opt.v}
              data-testid={`qf-proptype-${opt.v}`}
              className={`flex items-center gap-2 border border-charcoal/20 px-4 py-2.5 cursor-pointer transition-colors ${
                form.property_type === opt.v ? "bg-charcoal text-cream" : "bg-cream text-charcoal"
              }`}
            >
              <RadioGroupItem value={opt.v} className="border-current" />
              <span className="font-accent uppercase text-sm tracking-wide">{opt.l}</span>
            </label>
          ))}
        </RadioGroup>
      </div>

      <div>
        <Label className="font-accent uppercase text-xs tracking-widest">
          Type of Service Needed *
        </Label>
        <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
          <SelectTrigger className={`${inputClass} mt-1.5`} data-testid="qf-service-type">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {serviceTypeOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="qf-desc" className="font-accent uppercase text-xs tracking-widest">
          Brief Description of the Issue
        </Label>
        <Textarea
          id="qf-desc"
          data-testid="qf-description"
          className="border border-charcoal/20 rounded-none bg-cream focus-visible:ring-rust focus-visible:ring-offset-0 mt-1.5 min-h-[110px] text-base"
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
          placeholder="Tell us about the leak, damage, or project…"
        />
      </div>

      {/* Photo upload */}
      <div>
        <Label className="font-accent uppercase text-xs tracking-widest">
          Photo Upload (optional)
        </Label>
        <div className="mt-1.5">
          <button
            type="button"
            data-testid="qf-photo-btn"
            onClick={() => fileRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 border border-dashed border-charcoal/30 bg-cream-200 py-5 hover:bg-cream transition-colors"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <ImagePlus className="w-5 h-5" />
            )}
            <span className="font-accent uppercase text-sm tracking-wide">
              {uploading ? "Uploading…" : "Add photos of the issue"}
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
            data-testid="qf-photo-input"
          />
          {photos.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="qf-photo-list">
              {photos.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-2 border border-charcoal/20 bg-cream px-3 py-2 text-sm"
                >
                  <span className="flex items-center gap-2 truncate">
                    <Upload className="w-4 h-4 text-rust shrink-0" />
                    <span className="truncate">{p.original_filename}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removePhoto(p.id)}
                    className="text-charcoal/60 hover:text-rust"
                    aria-label="Remove photo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-accent uppercase text-xs tracking-widest">
            Preferred Contact Method
          </Label>
          <RadioGroup
            value={form.preferred_contact}
            onValueChange={(v) => update("preferred_contact", v)}
            className="flex gap-3 mt-2"
          >
            {["Phone call", "Email"].map((opt) => (
              <label
                key={opt}
                data-testid={`qf-contact-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-2 border border-charcoal/20 px-4 py-2.5 cursor-pointer transition-colors ${
                  form.preferred_contact === opt ? "bg-charcoal text-cream" : "bg-cream text-charcoal"
                }`}
              >
                <RadioGroupItem value={opt} className="border-current" />
                <span className="font-accent uppercase text-sm tracking-wide">{opt}</span>
              </label>
            ))}
          </RadioGroup>
        </div>
        <div>
          <Label className="font-accent uppercase text-xs tracking-widest">
            Preferred Appointment Time
          </Label>
          <Select value={form.preferred_time} onValueChange={(v) => update("preferred_time", v)}>
            <SelectTrigger className={`${inputClass} mt-1.5`} data-testid="qf-time">
              <SelectValue placeholder="Choose a time" />
            </SelectTrigger>
            <SelectContent>
              {timeOptions.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <button
        type="submit"
        data-testid="qf-submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 bg-rust text-cream border border-charcoal/20 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all font-heading text-2xl uppercase py-4 disabled:opacity-70 disabled:translate-y-0"
      >
        {submitting ? <Loader2 className="w-6 h-6 animate-spin" /> : "Request My Free Estimate"}
      </button>
      <p className="text-xs text-charcoal/60 text-center">
        Free estimates available. Inspections may have a fee. We'll never share your information.
      </p>
    </form>
  );
}
